<template>
  <div class="contest-entry-form-page" :class="{ 'has-scroll-layout': loadState === 'loaded' }">
    <LoadingSpinner v-if="loadState === 'loading'" size="medium" variant="inline" />

    <p v-else-if="loadState === 'error'" class="load-error" role="alert">
      {{ errorMessage }}
    </p>

    <template v-else-if="loadState === 'loaded'">
      <div class="layout">
        <aside class="nav-column">
          <div class="nav-top">
            <router-link class="back-link" :to="{ name: 'eventContest' }">
              <IconChevronLeft class="back-icon" />
              {{ t("contestEntryForm.backToContestList") }}
            </router-link>
          </div>
          <ContestEntryFormStepper :current-step="currentStep" />
        </aside>

        <section class="main-column">
          <div class="form-panel">
            <h1 class="form-title">{{ title }}</h1>
            <p v-if="description" class="form-description">{{ description }}</p>
            <slot />
          </div>
        </section>

        <ContestEntryFormContestAside :contest="contest" />
      </div>
    </template>
  </div>
</template>

<script lang="ts" setup>
import { defineProps } from "vue";
import { useI18n } from "vue-i18n-bridge";
import { NolaPfMicroCmsContest } from "@/lib/models/nolaPfContest";
import LoadingSpinner from "@/components/atoms/LoadingSpinner.vue";
import ContestEntryFormContestAside from "@/components/organisms/event/ContestEntryFormContestAside.vue";
import ContestEntryFormStepper from "@/components/organisms/event/ContestEntryFormStepper.vue";
import IconChevronLeft from "~icons/figma/ChevronLeft";

defineProps<{
  loadState: "loading" | "loaded" | "error";
  errorMessage: string;
  currentStep: 1 | 2 | 3;
  title: string;
  description?: string;
  contest: NolaPfMicroCmsContest | null;
}>();

const { t } = useI18n();
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";
@import "./contest-entry-form-layout";

.contest-entry-form-page {
  box-sizing: border-box;
  width: 100%;
  max-width: 1600px;
  margin-right: auto;
  margin-left: auto;
  min-height: 0;
  padding: 40px 48px 48px;
  overflow: auto;
  background-color: var(--color-background-normal);
  color: var(--color-text-normal);

  &.has-scroll-layout {
    display: flex;
    flex-direction: column;
    height: calc(100vh - #{$header-height});
    padding: 0 48px;
    overflow: hidden;
  }
}

.layout {
  display: flex;
  flex: 1;
  align-items: flex-start;
  gap: 24px;
  min-height: 0;
}

.nav-column {
  flex-shrink: 0;
  width: 232px;
}

.nav-top {
  margin-bottom: 0;
}

.back-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 12px;
  color: var(--color-text-normal);
  font-size: var(--font-size-sm);
  line-height: var(--line-height-default);
  text-decoration: none;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--color-focus-ring);
    border-radius: var(--radius-xs);
  }
}

.back-icon {
  font-size: 12px;
}

.main-column {
  flex: 1;
  align-self: stretch;
  min-width: 0;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
}

.form-panel {
  box-sizing: border-box;
  padding: 40px;
  border-radius: var(--radius-lg);
  background-color: var(--color-surface-base);
}

.form-title {
  margin: 0 0 24px;
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
  line-height: var(--line-height-default);
}

.form-description {
  margin: -16px 0 24px;
  font-size: var(--font-size-sm);
  line-height: var(--line-height-default);
}

.load-error {
  margin: 0;
  font-size: var(--font-size-sm);
  line-height: var(--line-height-default);
  color: var(--color-warning);
}

@include mq("sp") {
  .contest-entry-form-page {
    padding: 0;
    overflow: auto;
    background-color: var(--color-background-normal);

    &.has-scroll-layout {
      height: calc(100vh - #{$header-height-sp});
      padding: 0;
      overflow: hidden;
    }
  }

  .load-error {
    padding: 24px 16px;
  }

  .layout {
    flex-direction: column;
    gap: 0;
    min-height: 0;
  }

  .nav-column {
    flex-shrink: 0;
    width: 100%;
    padding: 24px 0 0;
    background-color: var(--color-background-normal);
  }

  .nav-top {
    padding: 0 16px 16px;
  }

  .back-link {
    margin-bottom: 0;
  }

  .main-column {
    flex: 1;
    width: 100%;
    min-height: 0;
  }

  .form-panel {
    padding: 24px 16px 32px;
    border-radius: 0;
    background-color: var(--color-surface-base);
  }

  .form-title {
    line-height: 1.5;
  }
}
</style>
